import classNames from "classnames";
import {
  getIconClass,
  PointIcon,
} from "@graphistry/viz/src/components/icon/icon.js";
import { NeighborhoodIcon } from "@graphistry/viz/src/components/icons";
import {
  LabelSingleSummary,
  PrimaryInteractions,
} from "@graphistry/viz/src/components/labels/label-single-summary.js";

import { Expander } from "./expander";
import styles from "./styles.less";

const getField = (key, columns = []) =>
  columns.filter((v) => v.key === key)?.[0]?.value ?? "&nbsp;";

const preferredIdKeys = new Set(['title', '_title', 'name', 'label', 'id', '_id']);
const getIdentifierColumn = (cols) => {
  const availablePreferredCols = new Map();
  cols.forEach(col => {
    if(preferredIdKeys.has(col.key)) {
      availablePreferredCols.set(col.key, col);
    }
  });

  if(availablePreferredCols.size) {
    return availablePreferredCols.get(preferredIdKeys.values().find(id => availablePreferredCols.has(id)));
  }

  // if no preferred col found then return first col
  return cols[0];
};

const InjectHtmlSpan = ({ html, ...props }) => (
  <span dangerouslySetInnerHTML={{ __html: html ?? "&nbsp;" }} {...props} />
);

// This should really be done in view.js - but panel injects components.
const SpacerFlexGrow = () => <div className={styles.spacerFlexGrow} />;

const Header = ({ rgb, iconClass, title, selectionType, columns = [] }) => {
  return selectionType === "point" ? (
    <div className={classNames(styles.header, styles[selectionType])}>
      <div className={styles.node}>
        <PointIcon
          type={selectionType}
          iconSize={30}
          iconClass={iconClass}
          pointRgb={rgb}
          renderPoint={true}
        />
      </div>
      <div className={styles.title}>
        <InjectHtmlSpan html={title} />
      </div>
    </div>
  ) : (
    <div className={classNames(styles.header, styles[selectionType])}>
      <div className={styles.endpoints}>
        <InjectHtmlSpan
          className={styles.source}
          html={getField("source", columns)}
        />
        <span className={styles.edgeIcon}>
          <i className="fa fa-long-arrow-right" />
        </span>
        <InjectHtmlSpan
          className={styles.destination}
          html={getField("destination", columns)}
        />
      </div>
      <div className={styles.title}>
        <InjectHtmlSpan html={title} />
      </div>
    </div>
  );
};

export const MultiHeader = ({ points, edges }) => (
  <div className={classNames(styles.header, styles.multi)}>
    <NeighborhoodIcon />
    <div className={styles.counts}>
      <span>
        <b>{points.length}</b>&nbsp;nodes
      </span>
      <span>
        <b>{edges.length}</b>&nbsp;edges
      </span>
    </div>
  </div>
);

export const Inspector = (props) => {
  console.debug(props, "@Inspector");

  const {
    encodings,
    type,
    title,
    columns = [],
    expander,
    setExpanderHops,
    setExpanderDirection,
    setExpanderOpen,
    commitExpansion,
    globalSelection,
    onCreateSets,
    collections,
  } = props;
  const iconClass = getIconClass({ encodings, type, columns });

  const showCreateSets = collections?.enabled && onCreateSets;

  const [r, g, b] = (props.index > -1 &&
    props.getColorForIndex(props.index)) || [0, 0, 0];
  const rgb = { r: r || 0, g: g || 0, b: b || 0 };

  const toggleExpander = () =>
    setExpanderOpen({open: !expander.open});

  const identifierColumn = getIdentifierColumn(columns);

  return (
    <>
      <div className={styles.inspectorContainer}>
        <Header
          rgb={rgb}
          title={title}
          columns={columns}
          selectionType={type}
          iconClass={iconClass}
        />
        <PrimaryInteractions
          {...props}
          interactionDetails={{
            type: 'gfql_chain',
            name: identifierColumn.key,
            componentType: type,
            ...identifierColumn
          }}
          toClipboard={title}
          toggleExpander={toggleExpander}
          onCreateSets={showCreateSets ? onCreateSets : undefined}
          collections={collections}
        >
          {expander.open && (
            <Expander
              hops={expander.hops}
              direction={expander.direction}
              setExpanderHops={setExpanderHops}
              setExpanderDirection={setExpanderDirection}
              commitExpansion={commitExpansion}
              startingPoints={globalSelection.point}
              startingEdges={globalSelection.edge}
            />
          )}
        </PrimaryInteractions>
        <LabelSingleSummary
          {...props}
          className={classNames(styles.yScrollContainer, {
            [styles['with-collections']]: collections?.enabled
          })}
          rgb={rgb}
          iconClass={iconClass}
          showPropertyListHeaders={true}
        />
      </div>
      {/* This is to create the spacer, should really be done with the view, but panel injects components. */}
      <SpacerFlexGrow />
    </>
  );
};

export const MultiInspector = (props) => {
  const {
    onExclude,
    onFilter,
    onCreateSets,
    points,
    edges,
    expander,
    setExpanderHops,
    setExpanderDirection,
    setExpanderOpen,
    commitExpansion,
    collections,
  } = props;

  const showCreateSets = collections?.enabled && onCreateSets;

  const toggleExpander = () =>
    setExpanderOpen({open: !expander.open});

  return (
    <>
      <div className={styles.inspectorContainer}>
        <MultiHeader points={points} edges={edges} />
        <PrimaryInteractions
          {...props}
          onExclude={onExclude}
          onFilter={onFilter}
          onCreateSets={showCreateSets ? onCreateSets : undefined}
          interactionDetails={{
            name: "_id",
            dataType: "isOneOf",
            componentType: "point",
            type: 'index_list',
            nodes: points, // for collections
            edges, // for collections
            value: points, // for backwards compatibility with filter and exclude
          }}
          toggleExpander={toggleExpander}
          collections={collections}
        >
          {expander.open && (
            <Expander
              hops={expander.hops}
              direction={expander.direction}
              setExpanderHops={setExpanderHops}
              setExpanderDirection={setExpanderDirection}
              commitExpansion={commitExpansion}
              startingPoints={points}
              startingEdges={edges}
            />
          )}
        </PrimaryInteractions>
      </div>
      {/* This is to create the spacer, should really be done with the view, but panel injects components. */}
      <SpacerFlexGrow />
    </>
  );
};
